import * as React from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import axios from "axios";
import { useEffect, useState } from "react";
import ConfirmationDialog from "./ConfirmationDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Paper } from "@mui/material";
import EditIcon from "@material-ui/icons/Edit";

import { useSelector, useDispatch } from "react-redux";
import {
  makeStyles,
  createMuiTheme,
  ThemeProvider,
} from "@material-ui/core/styles";

import { CssBaseline } from "@material-ui/core";
import Table from "@material-ui/core/Table";

import { setCustomisation } from "../redux/slices/customisationSlice";
import EditCustomisationForm from "./EditCustomisationForm";
import TableStatus from "./TableStatus";

const theme = createMuiTheme({
  spacing: 5,
});

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDataGrid-row.Mui-selected": {
      backgroundColor: "#e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#fafafa",
    },
    "& .MuiDataGrid-cell": {
      fontWeight: "normal",
      fontSize: "0.875rem",
      color: "rgba(0, 0, 0, 0.87)",
      letterSpacing: "0.01071em",
    },
    "& .MuiDataGrid-cell--textLeft": {
      textAlign: "left",
    },
    "& .MuiDataGrid-cell--textCenter": {
      textAlign: "center",
    },
    "& .MuiDataGrid-cell--textRight": {
      textAlign: "right",
    },
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: "#fafafa",
    },
    "& .MuiDataGrid-iconSeparator": {
      display: "none",
    },
  },
  table: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: `calc(100% - ${theme.spacing(9)}px)`,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  paper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    alignItems: "center",

    borderRadius: "4px",
  },
}));

const Customisation = ({ selectedOption, errors, isLoading }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const classes = useStyles();
  const dispatch = useDispatch();
  const headers = useSelector((state) => state.header);
  const customisationData = useSelector((state) => state.customisations);
  const emmsheaders = useSelector((state) => state.emmsheader);

  const [selectedItemId, setSelectedItemId] = useState(null);
  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "logoUrl",
      headerName: "Logo URL",
      flex: 1.5,
      minWidth: 300,
    },
    {
      field: "components",
      headerName: "Components",
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(params.row.id)}
          disabled={loading}
        >
          {loading ? "Deleting..." : "Delete"}
        </Button>
      ),
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          color="primary"
          onClick={() => handleEdit(params.row.id)}
          disabled={loading}
        >
          {loading ? "Editing..." : "Edit"}
        </Button>
      ),
    },
  ];

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const handleDelete = (id) => {
    setDeleteItemId(id);
    setShowConfirmationDialog(true);
  };
  const handleEdit = (id) => {
    setSelectedItemId(id);
  };

  const handleConfirmDelete = () => {
    setShowConfirmationDialog(false);
    setLoading(true);
    const requestBody = {
      path: "customisation",
      query: { id: `${deleteItemId}` },
      emmsId: selectedOption,
      body: {},
      requestType: "DELETE",
      headers: emmsheaders?.emmsheaders?.headers,
    };

    const api = localStorage.getItem("api");
    axios
      .post(api + `/emms/piggyback`, requestBody, headers)
      .then(() => {
        setRows((prevRows) => {
          const updatedRows = prevRows?.filter(
            (row) => row.id !== deleteItemId
          );
          dispatch(setCustomisation(updatedRows));
          return updatedRows;
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancelDelete = () => {
    setShowConfirmationDialog(false);
  };

  useEffect(() => {
    if (customisationData.customisations)
      setRows(customisationData.customisations);
  }, [customisationData.customisations]);
  return (
    <>
      {rows?.length > 0 && errors === false ? (
        <CssBaseline>
          <ThemeProvider>
            <div className="emms-data-grid-container"   style={{ padding: "2.5% 10%", overflowX: "auto" }}>
              <DataGrid
                  rows={rows}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  pageSizeOptions={[5]}
                  checkboxSelection
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                />
            </div>
            {selectedItemId !== null && (
              <EditCustomisationForm
                customisationData={customisationData.customisations}
                selectedOption={selectedOption}
                selectedItemId={selectedItemId}
                handleClose={() => setSelectedItemId(null)} 
              />
            )}

            {showConfirmationDialog && (
              <ConfirmationDialog
                open={showConfirmationDialog}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
              />
            )}
          </ThemeProvider>
        </CssBaseline>
      ) : (
        <TableStatus loading={isLoading} errors={errors} />
      )}
    </>
  );
};

export default Customisation;
